var lib_core = require("lib-core");
var lib_geometry = require("lib-geometry");
var cio_config = require("cio-config");
var PathType = require("./pathType.js");

var PathObjectData = function (point, type, tan_0, tan_1, xRadius, yRadius, largeArcFlag, sweepFlag) {
    this.makeObservableTreeNode();

    this.endpoint = this.value("endpoint", true, lib_geometry.Coord.validator);
    this.tan_0 = this.value("tan_0", true, lib_geometry.Coord.validator);
    this.tan_1 = this.value("tan_1", true, lib_geometry.Coord.validator);
    this.type  = this.value("type", true);

    this.xRadius = this.value("xRadius", true, cio_config.types.float.validator);
    this.yRadius = this.value("yRadius", true, cio_config.types.float.validator);
    this.angle = this.value("angle", true, cio_config.types.float.validator);
    this.largeArcFlag = this.value("largeArcFlag", true, cio_config.types.any.validator);
    this.sweepFlag = this.value("sweepFlag", true, cio_config.types.any.validator);

    if(point != undefined) this.endpoint(point);
    else this.endpoint(new lib_geometry.Coord(0, 0));
    if(type != undefined) this.type(type);
    else this.type(PathType.POINT);
    if(tan_0 != undefined) this.tan_0(tan_0);
    if(tan_1 != undefined) this.tan_1(tan_1);
    if(xRadius != undefined) this.xRadius(xRadius);
    if(yRadius != undefined) this.yRadius(yRadius);
    if(largeArcFlag != undefined) this.largeArcFlag(largeArcFlag);
    if(sweepFlag != undefined) this.sweepFlag(sweepFlag);
};
lib_core.mixin(PathObjectData, lib_core.ObservableTreeNode);
lib_core.JsonParser.registerClass(PathObjectData, "PathObjectData");

module.exports = PathObjectData;

//todo Can this be removed?
PathObjectData.prototype.getEndpoint = function () {
    return this.endpoint();
};

PathObjectData.prototype.index = function () {
    return this.parent().objects.indexOf(this);
};

PathObjectData.prototype.next = function () {
    var index = this.index() + 1;
    if(this.parent().closed()) {
        if (index >= this.parent().objects.size()) index = 0;
    }
    return this.parent().objects.get(index);
};

PathObjectData.prototype.previous = function () {
    var index = this.index() - 1;
    if(this.parent().closed()) {
        if (index < 0) index = this.parent().objects.size() - 1;
    } else {
        if (index < 0) return null;
    }
    return this.parent().objects.get(index);
};

PathObjectData.prototype.rotate = function (angle, center) {
    this.endpoint(this.endpoint().rotate(angle, center));
    if(this.tan_0()) this.tan_0(this.tan_0().rotate(angle, center));
    if(this.tan_1()) this.tan_1(this.tan_1().rotate(angle, center));
    this.angle(this.angle()+angle);
};

PathObjectData.prototype.move = function (vec) {
    this.endpoint(this.endpoint().add(vec));
    if(this.tan_0()) this.tan_0(this.tan_0().add(vec));
    if(this.tan_1()) this.tan_1(this.tan_1().add(vec));
};

PathObjectData.prototype.flip = function (flipline) {
    this.endpoint(this.endpoint().flip(flipline));
    if(this.tan_0()) this.tan_0(this.tan_0().flip(flipline));
    if(this.tan_1()) this.tan_1(this.tan_1().flip(flipline));
    this.sweepFlag(!this.sweepFlag());

    var flipAngle = flipline.dir.angleDegrees()-this.angle();
    this.angle((this.angle()+flipAngle*2)%360);
};

/**
 * Find the center and focus positions for the ellipse underlying an arc
 * Algorithm used is as svg defines it: https://www.w3.org/TR/SVG/implnote.html#ArcConversionEndpointToCenter
 * @function findCandF
 * @author E. Pataky
 * @returns {[lib_geometry.Coord, lib_geometry.Coord]}
 */
PathObjectData.prototype.calculateCandF = function() {
    var self = this;

    var A = self.previous().getEndpoint();
    var B = self.getEndpoint();
    var c_coord = null, f_coord = null;

    // we would not call this unless it was an arc, but check anyway
    if(self.type() != PathType.ARC) {
        return [A.add(B).mult(0.5), A.add(B).mult(0.5)];
    }

    // we need to backcalculate the location of C and F
    var angle = self.angle(); // angle of ellipse
    var xR = self.xRadius();
    var yR = self.yRadius();
    var a, b;

    // very important to update angle used here in case yR > xR
    if(xR >= yR) {
        a = xR;
        b = yR;
    } else {
        a = yR;
        b = xR;
        angle = angle + 90;
    }

    var f = Math.sqrt(a*a - b*b);

    /**
     * Suppose ellipse with arbitrary points A and B on the ellipse,
     *   major and minor axes 2a and 2b, center C and focus F
     *   angle = angle from C to F, and c = the distance from C to F
     *   A', B' and F' are A, B, and F reflected across line perp to line
     *     through C from A to A', B to B' and F to F', respectively
     *
     * https://www.w3.org/TR/SVG/implnote.html#ArcConversionEndpointToCenter
     * SVG algorithm, Given:
     *   A=(x1,y1), B=(x2,y2)
     *   fA = large arc flag, fS = sweep flag
     *   phi = angle of ellipse to x axis
     *   rx = a, ry = b
     * Find: C = (cx, cy)
     */
    var xm = (A.x - B.x)/2.0;
    var ym = (A.y - B.y)/2.0;
    var cosAng = Math.cos(angle*Math.PI/180.0);
    var sinAng = Math.sin(angle*Math.PI/180.0);
    var x1p = xm*cosAng + ym*sinAng;
    var y1p = -xm*sinAng + ym*cosAng;

    // step 2 find Cx',Cy'
    var rx = a, ry = b, rx2 = rx*rx, ry2 = ry*ry, x1p2 = x1p*x1p, y1p2 = y1p*y1p;
    var sqrtof = (rx2*ry2 - rx2*y1p2 - ry2*x1p2) / (rx2*y1p2 + ry2*x1p2);
    if(sqrtof < 0)  //fallback (in case of small rounding errors and badly chosen rx and ry)
        sqrtof = 0;
    var cp_val = Math.sqrt(sqrtof);
    var Fs = self.sweepFlag(), Fa = self.largeArcFlag();
    var cxp;
    var cyp;
    if(Math.abs(rx)>1e-6 && Math.abs(ry)>1e-6) {
        cxp = (Fa != Fs ? +1 : -1)*cp_val*(rx*y1p/ry);
        cyp = (Fa != Fs ? +1 : -1)*cp_val*(-ry*x1p/rx);
    } else {
        cxp = 0;
        cyp = 0;
    }

    // step 3 find Cx, Cy
    xm = (A.x + B.x)/2.0;
    ym = (A.y + B.y)/2.0;
    var Cx = (cxp*cosAng - cyp*sinAng) + xm;
    var Cy = (cxp*sinAng + cyp*cosAng) + ym;

    c_coord = new lib_geometry.Coord(Cx, Cy);
    f_coord = c_coord.add(new lib_geometry.Coord(f, 0)).rotate(angle, c_coord);

    cp_val = Fs = cxp = cyp = Cx = Cy = null; // cleanup

    // cleanup
    xm = ym = cosAng = sinAng = x1p = y1p = null;
    rx = sqrtof = cyp = null;

    if (A.eq(B)) {  //if A==B, the ellipse/arc is not fully defined by the datamodel so not allowed
        c_coord = A;
        f_coord = A;
    }

    console.assert(lib_geometry.Coord.validator(c_coord), "calculated ellipse center point is invalid");
    console.assert(lib_geometry.Coord.validator(f_coord), "calculated ellipse focal point is invalid");
    var fp_coord = f_coord.flip(c_coord);
    return {c_coord: c_coord, f_coord: f_coord, fp_coord: fp_coord};
};

PathObjectData.prototype.setCandF = function(c_coord, f_coord) {
    console.assert(lib_geometry.Coord.validator(c_coord));
    console.assert(lib_geometry.Coord.validator(f_coord));
    // find arc xRadius and yRadius
    // The sum of the distances from any point P on the ellipse to those two foci is
    // constant and equal to the major axis (PF1 + PF2 = 2a)
    var fp_coord = f_coord.flip(c_coord);
    var PF1 = this.endpoint().distance(f_coord);
    var PF2 = this.endpoint().distance(fp_coord);
    var xRadius = (PF1 + PF2) / 2.0;
    var CtoFDist = c_coord.distance(f_coord);
    // The distance from the center C to either focus is f = ae, which can be expressed in
    // terms of the major and minor radii: CtoFDist = sqrt(a^2 - b^2)
    var yRadius = Math.sqrt(xRadius * xRadius - CtoFDist * CtoFDist);
    // the "angle" of the arc path object, is the angle of the underlying ellipse
    // which is the angle between focus F and C
    var angle = f_coord.sub(c_coord).angleDegrees();

    var right_side = lib_geometry.pointLineSide(this.previous().endpoint(), this.endpoint(), c_coord) > 0;
    var large_arc = (!!right_side) == (!!this.sweepFlag());

    console.assert(!isNaN(xRadius), "trying to set an invalid xRadius");
    console.assert(!isNaN(yRadius), "trying to set an invalid yRadius");
    console.assert(!isNaN(angle), "trying to set an invalid angle");
    //set the datamodel
    this.xRadius(xRadius);
    this.yRadius(yRadius);
    this.largeArcFlag(large_arc);
    this.angle(angle);
};

PathObjectData.prototype.largeRadius = function() {
    return Math.max(this.xRadius(), this.yRadius());
};

PathObjectData.prototype.smallRadius = function() {
    return Math.min(this.xRadius(), this.yRadius());
};

PathObjectData.prototype.largeArcAxisAngle = function() {
    return this.angle() + (this.xRadius() < this.yRadius() ? 90 : 0);
};

PathObjectData.prototype.isOuterPoint = function() {//begin or end of path
    return !(this.next() && this.previous());
};
